import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { NumericKeypad } from './NumericKeypad';
import { type Product, type OrderLineOption, getPortionPrice } from '../types';

interface TenantSettings {
  sectionOrder?: Record<string, number>;
  subSectionOrder?: Record<string, Record<string, number>>;
}

interface ProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  products: Product[];
  onConfirm: (selection: {
    productId: string;
    productName: string;
    quantity: number;
    options: OrderLineOption[];
    portionSizeId?: string;
  }) => void;
  tenantSettings?: TenantSettings | null;
}

interface ProductState {
  quantity: number;
  options: Record<string, boolean | string | string[]>; // optionId -> value (boolean, string for select, string[] for multiselect)
  portionSizeId?: string; // Selected portion size for kg products
}

export function ProductSelectionModal({
  open,
  onClose,
  products,
  onConfirm,
  tenantSettings,
}: ProductSelectionModalProps) {
  // État pour chaque produit
  const [productStates, setProductStates] = useState<Record<string, ProductState>>({});
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  // Group products by section, sorted by displayOrder
  const productsBySection = useMemo(() => {
    const grouped: Record<string, Product[]> = {};

    products.forEach((product) => {
      const section = product.section || 'Autres';
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(product);
    });

    // Sort products within each section by displayOrder
    Object.keys(grouped).forEach((section) => {
      grouped[section].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    });

    return grouped;
  }, [products]);

  // Sort sections using tenant settings or alphabetically
  const sections = useMemo(() => {
    const sectionKeys = Object.keys(productsBySection);
    if (tenantSettings?.sectionOrder) {
      return sectionKeys.sort((a, b) => {
        const orderA = tenantSettings.sectionOrder![a] ?? 999;
        const orderB = tenantSettings.sectionOrder![b] ?? 999;
        return orderA - orderB;
      });
    }
    return sectionKeys.sort();
  }, [productsBySection, tenantSettings]);

  // Get subSections grouped by section
  const subSectionsBySection = useMemo(() => {
    const result: Record<string, string[]> = {};
    products.forEach((p) => {
      const section = p.section || 'Autres';
      if (p.subSection) {
        if (!result[section]) result[section] = [];
        if (!result[section].includes(p.subSection)) {
          result[section].push(p.subSection);
        }
      }
    });
    // Sort subSections using tenant settings or alphabetically
    Object.keys(result).forEach((section) => {
      if (tenantSettings?.subSectionOrder?.[section]) {
        result[section].sort((a, b) => {
          const orderA = tenantSettings.subSectionOrder![section][a] ?? 999;
          const orderB = tenantSettings.subSectionOrder![section][b] ?? 999;
          return orderA - orderB;
        });
      } else {
        result[section].sort();
      }
    });
    return result;
  }, [products, tenantSettings]);

  // Group products by subsection within each section for display
  const productsBySubSection = useMemo(() => {
    const result: Record<string, { subSection: string | null; products: Product[] }[]> = {};

    Object.entries(productsBySection).forEach(([section, sectionProducts]) => {
      const subSections = subSectionsBySection[section] || [];

      if (subSections.length === 0) {
        // No subsections, just return all products in one group
        result[section] = [{ subSection: null, products: sectionProducts }];
      } else {
        // Group products by subsection
        const groups: { subSection: string | null; products: Product[] }[] = [];

        // First, products without subsection
        const productsWithoutSubSection = sectionProducts.filter(p => !p.subSection);
        if (productsWithoutSubSection.length > 0) {
          groups.push({ subSection: null, products: productsWithoutSubSection });
        }

        // Then, products grouped by subsection (in order)
        subSections.forEach(subSection => {
          const subSectionProducts = sectionProducts.filter(p => p.subSection === subSection);
          if (subSectionProducts.length > 0) {
            groups.push({ subSection, products: subSectionProducts });
          }
        });

        result[section] = groups;
      }
    });

    return result;
  }, [productsBySection, subSectionsBySection]);

  const getProductState = (productId: string): ProductState => {
    return productStates[productId] || { quantity: 0, options: {} };
  };

  const setProductQuantity = (productId: string, quantity: number, hasPortionSizes: boolean) => {
    // If product has portion sizes, quantity should be integer (count of portions)
    const roundedQuantity = hasPortionSizes
      ? Math.max(0, Math.round(quantity))
      : Math.max(0, Math.round(quantity * 1000) / 1000);

    setProductStates(prev => ({
      ...prev,
      [productId]: {
        ...getProductState(productId),
        quantity: roundedQuantity,
      }
    }));
  };

  const setProductPortionSize = (productId: string, portionSizeId: string) => {
    setProductStates(prev => {
      const currentState = getProductState(productId);
      // If changing portion size and quantity is 0, set to 1
      const newQuantity = currentState.quantity === 0 ? 1 : currentState.quantity;
      return {
        ...prev,
        [productId]: {
          ...currentState,
          quantity: newQuantity,
          portionSizeId,
        }
      };
    });
  };

  // Toggle boolean option
  const toggleBooleanOption = (productId: string, optionId: string) => {
    setProductStates(prev => {
      const currentState = getProductState(productId);
      const currentValue = currentState.options[optionId] || false;
      return {
        ...prev,
        [productId]: {
          ...currentState,
          options: {
            ...currentState.options,
            [optionId]: !currentValue,
          }
        }
      };
    });
  };

  // Select single choice (for 'select' type)
  const handleSelectChange = (productId: string, optionId: string, choiceId: string) => {
    setProductStates(prev => {
      const currentState = getProductState(productId);
      return {
        ...prev,
        [productId]: {
          ...currentState,
          options: {
            ...currentState.options,
            [optionId]: choiceId,
          }
        }
      };
    });
  };

  // Toggle choice in multiselect
  const handleMultiselectToggle = (productId: string, optionId: string, choiceId: string, maxSelections?: number) => {
    setProductStates(prev => {
      const currentState = getProductState(productId);
      const currentValue = currentState.options[optionId];
      const currentArray = Array.isArray(currentValue) ? currentValue : [];

      let newArray: string[];
      if (currentArray.includes(choiceId)) {
        // Remove choice
        newArray = currentArray.filter(id => id !== choiceId);
      } else {
        // Add choice (respect max selections)
        if (maxSelections && currentArray.length >= maxSelections) {
          return prev; // Don't add if at max
        }
        newArray = [...currentArray, choiceId];
      }

      return {
        ...prev,
        [productId]: {
          ...currentState,
          options: {
            ...currentState.options,
            [optionId]: newArray,
          }
        }
      };
    });
  };

  // Check if a multiselect choice is disabled (max reached and not selected)
  const isMultiselectChoiceDisabled = (productId: string, optionId: string, choiceId: string, maxSelections?: number): boolean => {
    if (!maxSelections) return false;
    const state = getProductState(productId);
    const currentValue = state.options[optionId];
    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    return currentArray.length >= maxSelections && !currentArray.includes(choiceId);
  };

  // Check if all required select options have been chosen for a product
  const areSelectOptionsValidForProduct = (product: Product): boolean => {
    const state = getProductState(product.id);
    const availableOptions = product.availableOptions || [];

    for (const option of availableOptions) {
      if (option.type === 'select' && option.choices && option.choices.length > 0) {
        const value = state.options[option.id];
        if (!value || value === '') {
          return false;
        }
      }
    }
    return true;
  };

  const handleSaveAll = () => {
    // Récupérer tous les produits avec une quantité > 0 et des options valides
    const selectedProducts = products.filter(p => {
      const state = getProductState(p.id);
      return state.quantity > 0 && areSelectOptionsValidForProduct(p);
    });

    // Ajouter chaque produit sélectionné
    selectedProducts.forEach(product => {
      const state = getProductState(product.id);

      // Convert options object to array, filtering out empty values
      const options: OrderLineOption[] = [];
      for (const [id, value] of Object.entries(state.options)) {
        // Skip falsy boolean values
        if (value === false || value === undefined) continue;
        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0) continue;
        // Skip empty strings
        if (value === '') continue;

        options.push({ id, value });
      }

      onConfirm({
        productId: product.id,
        productName: product.name,
        quantity: state.quantity,
        options,
        portionSizeId: state.portionSizeId,
      });
    });

    // Reset et fermer
    setProductStates({});
    onClose();
  };

  const handleClose = () => {
    setProductStates({});
    onClose();
  };

  // Compter le nombre de produits sélectionnés et valides
  const selectedCount = products.filter(p => {
    const state = getProductState(p.id);
    return state.quantity > 0 && areSelectOptionsValidForProduct(p);
  }).length;

  // Compter les produits avec quantité > 0 mais options manquantes
  const incompleteCount = products.filter(p => {
    const state = getProductState(p.id);
    return state.quantity > 0 && !areSelectOptionsValidForProduct(p);
  }).length;

  const renderProductCard = (product: Product) => {
    const state = getProductState(product.id);
    const hasOptions = product.availableOptions && product.availableOptions.length > 0;
    const hasQuantity = state.quantity > 0;
    const hasPortionSizes = product.portionSizes && product.portionSizes.length > 0;
    const isWeightWithoutPortions = (product.saleMode === 'WEIGHT' || product.saleMode === 'WEIGHT_ESTIMATED') && !hasPortionSizes;
    const isValid = areSelectOptionsValidForProduct(product);
    const hasIncompleteOptions = hasQuantity && !isValid;

    // Get the selected portion size label for display
    const selectedPortion = hasPortionSizes && state.portionSizeId
      ? product.portionSizes!.find(p => p.id === state.portionSizeId)
      : null;

    // Quantity display
    const quantityDisplay = hasPortionSizes
      ? (selectedPortion ? `${state.quantity}× ${selectedPortion.label}` : `${state.quantity}×`)
      : (isWeightWithoutPortions ? `${state.quantity.toFixed(3)} kg` : `${state.quantity}`);

    // Card styling based on state: incomplete (orange), valid with quantity (green), or default (gray)
    const cardClassName = hasIncompleteOptions
      ? 'border-orange-500 bg-orange-50'
      : hasQuantity
        ? 'border-green-500 bg-green-50'
        : 'border-gray-200 bg-white';

    return (
      <div
        key={product.id}
        className={`p-3 border-2 rounded-lg transition-all ${cardClassName}`}
      >
        <div className="font-medium mb-3 text-center">{product.shortenName ?? product.name}</div>

        {/* Portion size selector for kg products with portions */}
        {hasPortionSizes && (
          <div className="mb-3 space-y-1">
            <div className="text-xs font-medium text-gray-600 text-center">Format</div>
            <div className="grid grid-cols-2 gap-1">
              {product.portionSizes!.map((portion) => {
                const isSelected = state.portionSizeId === portion.id;
                const portionPrice = getPortionPrice(product, portion.id);
                return (
                  <button
                    key={portion.id}
                    onClick={() => setProductPortionSize(product.id, portion.id)}
                    className={`px-2 py-1.5 text-xs rounded border transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-100 text-green-800 font-medium'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div>{portion.label}</div>
                    {portionPrice !== null && (
                      <div className="text-[10px] text-gray-500">{portionPrice.toFixed(2)}€</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Boutons quantité */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setProductQuantity(product.id, state.quantity - (isWeightWithoutPortions ? 0.1 : 1), hasPortionSizes ?? false)}
            disabled={state.quantity === 0}
            className="h-10 w-10 p-0 text-xl"
          >
            −
          </Button>

          <Popover open={openPopoverId === product.id} onOpenChange={(open) => setOpenPopoverId(open ? product.id : null)}>
            <PopoverTrigger asChild>
              <div
                className={`text-xl font-bold min-w-20 text-center cursor-pointer hover:bg-gray-100 rounded px-2 py-1 select-none ${hasQuantity ? 'text-green-700' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                {quantityDisplay}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center" sideOffset={5}>
              <NumericKeypad
                value={state.quantity}
                onValueChange={(val) => setProductQuantity(product.id, val, hasPortionSizes ?? false)}
                onConfirm={() => setOpenPopoverId(null)}
                allowDecimals={isWeightWithoutPortions}
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setProductQuantity(product.id, state.quantity + (isWeightWithoutPortions ? 0.1 : 1), hasPortionSizes ?? false)}
            className="h-10 w-10 p-0 text-xl"
          >
            +
          </Button>
        </div>

        {/* Dynamic options from product.availableOptions */}
        {hasOptions && hasQuantity && (
          <div className="space-y-3 pt-2 border-t">
            {product.availableOptions!.map((option) => (
              <div key={option.id}>
                {/* Boolean option */}
                {option.type === 'boolean' && (
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={state.options[option.id] === true}
                      onCheckedChange={() => toggleBooleanOption(product.id, option.id)}
                    />
                    <span>{option.label}</span>
                  </label>
                )}

                {/* Select option - radio buttons (required) */}
                {option.type === 'select' && option.choices && (
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-gray-600">
                      {option.label}
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                    <div className="space-y-1">
                      {option.choices.map((choice) => (
                        <label key={choice.id} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name={`${product.id}-${option.id}`}
                            checked={state.options[option.id] === choice.id}
                            onChange={() => handleSelectChange(product.id, option.id, choice.id)}
                            className="w-3.5 h-3.5 text-green-600 focus:ring-green-500"
                          />
                          <span>{choice.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Multiselect option - checkboxes */}
                {option.type === 'multiselect' && option.choices && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-medium text-gray-600">{option.label}</span>
                      {option.maxSelections && (
                        <span className="text-xs text-gray-400">(max {option.maxSelections})</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {option.choices.map((choice) => {
                        const optionValue = state.options[option.id];
                        const isChecked = Array.isArray(optionValue) && optionValue.includes(choice.id);
                        const isDisabled = isMultiselectChoiceDisabled(product.id, option.id, choice.id, option.maxSelections);

                        return (
                          <label
                            key={choice.id}
                            className={`flex items-center gap-2 text-sm ${
                              isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          >
                            <Checkbox
                              checked={isChecked}
                              disabled={isDisabled}
                              onCheckedChange={() => handleMultiselectToggle(product.id, option.id, choice.id, option.maxSelections)}
                            />
                            <span>{choice.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
        {/* Header sticky */}
        <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 pt-6 pb-4 space-y-0">
          <div className="flex flex-row items-center justify-between">
            <DialogTitle>Ajouter des produits</DialogTitle>
            <div className="flex gap-2">
              {selectedCount > 0 && (
                <Button
                  onClick={handleSaveAll}
                  className="bg-green-600 hover:bg-green-700"
                >
                  ✓ Ajouter ({selectedCount})
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleClose}
                className="px-3"
              >
                ✕
              </Button>
            </div>
          </div>
          {incompleteCount > 0 && (
            <div className="text-sm text-orange-600 mt-2">
              {incompleteCount} produit{incompleteCount > 1 ? 's' : ''} avec options obligatoires manquantes
            </div>
          )}
        </DialogHeader>

        {/* Zone scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {sections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun produit disponible
            </div>
          ) : sections.length === 1 ? (
            // Single section - no tabs needed
            <div className="space-y-6">
              {productsBySubSection[sections[0]]?.map((group, groupIndex) => (
                <div key={group.subSection || `no-subsection-${groupIndex}`}>
                  {group.subSection && (
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 pl-1 border-b pb-2">
                      {group.subSection.charAt(0).toUpperCase() + group.subSection.slice(1)}
                    </h3>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {group.products.map((product) => renderProductCard(product))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Multiple sections - use tabs
            <Tabs defaultValue={sections[0]} className="w-full">
              <div className="overflow-x-auto -mx-1 px-1">
                <TabsList className="inline-flex w-max min-w-full">
                  {sections.map((section) => (
                    <TabsTrigger key={section} value={section} className="flex-1 min-w-fit px-4">
                      {section.charAt(0).toUpperCase() + section.slice(1)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {sections.map((section) => (
                <TabsContent key={section} value={section} className="mt-4">
                  {productsBySection[section].length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Aucun produit dans cette section
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {productsBySubSection[section]?.map((group, groupIndex) => (
                        <div key={group.subSection || `no-subsection-${groupIndex}`}>
                          {group.subSection && (
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 pl-1 border-b pb-2">
                              {group.subSection.charAt(0).toUpperCase() + group.subSection.slice(1)}
                            </h3>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            {group.products.map((product) => renderProductCard(product))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
